/**
 * Talking to the server.
 *
 * Every call returns a plain result rather than throwing, so a screen always has
 * something sensible to show — docs/06-UI-SPEC.md §17.4: never a technical error.
 */

import type { GradeBand } from '@opal/shared';

import type { Lecture } from './lectures.js';

const OFFLINE_MESSAGE = "We couldn't reach the server. Please try again.";

export interface StartedLesson {
  readonly boardId: string;
  readonly roomCode: string;
  readonly topic: string | null;
  readonly gradeBand: GradeBand;
}

export interface JoinedLesson {
  readonly boardId: string;
  readonly displayName: string;
  /** The student's place in the class. Presented when the board is opened (SRS D4). */
  readonly participantId: string;
}

/** One person in the class, as the participants panel shows them. */
export interface ClassMember {
  readonly id: string;
  readonly displayName: string;
  readonly canEdit: boolean;
}

/** The class as the teacher sees it — docs/06-UI-SPEC.md §6.4. */
export interface Classroom {
  readonly locked: boolean;
  readonly members: readonly ClassMember[];
}

export type ApiResult<T> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly message: string };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function messageFrom(body: unknown, fallback: string): string {
  return isRecord(body) && typeof body.message === 'string' ? body.message : fallback;
}

async function get(path: string): Promise<{ status: number; body: unknown }> {
  const response = await fetch(path, { credentials: 'same-origin' });
  const body: unknown = await response.json().catch(() => null);
  return { status: response.status, body };
}

async function post(path: string, payload: unknown): Promise<{ status: number; body: unknown }> {
  const response = await fetch(path, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const body: unknown = await response.json().catch(() => null);
  return { status: response.status, body };
}

/** Creates the board and the class the students will join. */
export async function startLesson(
  topic: string,
  gradeBand: GradeBand,
): Promise<ApiResult<StartedLesson>> {
  try {
    const { status, body } = await post('/api/lessons', { topic, gradeBand });
    if (status !== 201 || !isRecord(body)) {
      return { ok: false, message: messageFrom(body, "We couldn't start the lesson.") };
    }
    const { boardId, roomCode } = body;
    if (typeof boardId !== 'string' || typeof roomCode !== 'string') {
      return { ok: false, message: "We couldn't start the lesson." };
    }
    return {
      ok: true,
      value: {
        boardId,
        roomCode,
        topic: typeof body.topic === 'string' ? body.topic : null,
        gradeBand,
      },
    };
  } catch {
    return { ok: false, message: OFFLINE_MESSAGE };
  }
}

/** True when the typed code belongs to a class that is running right now. */
export async function checkRoomCode(code: string): Promise<boolean> {
  try {
    const { body } = await post('/api/join/check', { code });
    return isRecord(body) && body.matches === true;
  } catch {
    return false;
  }
}

/** Puts the student into the class. */
export async function joinLesson(code: string, name: string): Promise<ApiResult<JoinedLesson>> {
  try {
    const { status, body } = await post('/api/join', { code, name });
    if (status !== 200 || !isRecord(body)) {
      return { ok: false, message: messageFrom(body, "We couldn't join that class.") };
    }
    const { boardId, displayName, participantId } = body;
    if (
      typeof boardId !== 'string' ||
      typeof displayName !== 'string' ||
      typeof participantId !== 'string'
    ) {
      return { ok: false, message: "We couldn't join that class." };
    }
    return { ok: true, value: { boardId, displayName, participantId } };
  } catch {
    return { ok: false, message: OFFLINE_MESSAGE };
  }
}

// ---------------------------------------------------------------------------
// The teacher's controls for a running class — SRS D4
// ---------------------------------------------------------------------------

const CLASS_PROBLEM = "We couldn't reach your class list.";

function readMember(value: unknown): ClassMember | null {
  if (!isRecord(value)) return null;
  const { id, displayName, canEdit } = value;
  if (typeof id !== 'string' || typeof displayName !== 'string') return null;
  return { id, displayName, canEdit: canEdit === true };
}

function readClassroom(body: unknown): Classroom | null {
  if (!isRecord(body) || !Array.isArray(body.participants)) return null;
  const members: ClassMember[] = [];
  for (const entry of body.participants) {
    const member = readMember(entry);
    if (member) members.push(member);
  }
  return { locked: body.locked === true, members };
}

async function classroomCall(
  call: () => Promise<{ status: number; body: unknown }>,
): Promise<ApiResult<Classroom>> {
  try {
    const { status, body } = await call();
    const classroom = status === 200 ? readClassroom(body) : null;
    if (!classroom) return { ok: false, message: messageFrom(body, CLASS_PROBLEM) };
    return { ok: true, value: classroom };
  } catch {
    return { ok: false, message: OFFLINE_MESSAGE };
  }
}

/** Everyone in this board's class, and whether it is taking new joiners. */
export function fetchClassroom(boardId: string): Promise<ApiResult<Classroom>> {
  return classroomCall(() => get(`/api/boards/${boardId}/participants`));
}

/** SRS D4 — grants or takes away one student's drawing. */
export function setMemberEditing(
  boardId: string,
  participantId: string,
  canEdit: boolean,
): Promise<ApiResult<Classroom>> {
  return classroomCall(() =>
    post(`/api/boards/${boardId}/participants/${participantId}/editing`, { canEdit }),
  );
}

/** SRS D4 — removes a student from the class. Everything they drew stays. */
export function removeMember(
  boardId: string,
  participantId: string,
): Promise<ApiResult<Classroom>> {
  return classroomCall(() =>
    post(`/api/boards/${boardId}/participants/${participantId}/remove`, {}),
  );
}

/** SRS D4 — a locked class takes no new joiners. */
export function setClassLocked(
  boardId: string,
  locked: boolean,
): Promise<ApiResult<Classroom>> {
  return classroomCall(() => post(`/api/boards/${boardId}/lock`, { locked }));
}

/**
 * SRS I1 — ends the lesson.
 *
 * The server answers only once the board has been written for the last time, so this
 * resolving is what makes it safe to tell the teacher her lesson is saved.
 */
export async function endClass(boardId: string): Promise<ApiResult<true>> {
  try {
    const { status, body } = await post(`/api/boards/${boardId}/end`, {});
    if (status !== 200 || !isRecord(body) || body.ended !== true) {
      return { ok: false, message: messageFrom(body, "We couldn't end the class.") };
    }
    return { ok: true, value: true };
  } catch {
    return { ok: false, message: OFFLINE_MESSAGE };
  }
}

// ---------------------------------------------------------------------------
// Lessons that closed themselves — SRS I2
// ---------------------------------------------------------------------------

/** One lesson the teacher never closed, as the home page notice describes it. */
export interface RecoveredLesson {
  readonly boardId: string;
  readonly topic: string | null;
  /** Already written out by the server as `21 Aug 2026`, in the teacher's clock. */
  readonly date: string;
}

function readRecovered(value: unknown): RecoveredLesson | null {
  if (!isRecord(value)) return null;
  const { boardId, topic, date } = value;
  if (typeof boardId !== 'string' || typeof date !== 'string') return null;
  return { boardId, date, topic: typeof topic === 'string' ? topic : null };
}

/**
 * The teacher's lessons that were closed for them because nobody was left in them.
 *
 * An empty list on any problem at all. This is a notice, and a notice that cannot be
 * fetched must never be the reason the home page fails to appear.
 */
export async function fetchRecoveredLessons(): Promise<readonly RecoveredLesson[]> {
  try {
    const { status, body } = await get('/api/lessons/recovered');
    if (status !== 200 || !isRecord(body) || !Array.isArray(body.lessons)) return [];
    const lessons: RecoveredLesson[] = [];
    for (const entry of body.lessons) {
      const lesson = readRecovered(entry);
      if (lesson) lessons.push(lesson);
    }
    return lessons;
  } catch {
    return [];
  }
}

// ---------------------------------------------------------------------------
// Filing lessons to the teacher's Google Drive — SRS I4
// ---------------------------------------------------------------------------

/**
 * Where the teacher goes to allow the app to file lessons into their Drive.
 *
 * A full page visit rather than a fetch: it ends at Google's own consent screen, and
 * that is the only place this permission can be given.
 */
export const DRIVE_CONNECT_PATH = '/api/drive/connect';

/** Whether this teacher has allowed Drive yet. False if the question cannot be asked. */
export async function fetchDriveConnected(): Promise<boolean> {
  try {
    const { status, body } = await get('/api/drive/status');
    return status === 200 && isRecord(body) && body.connected === true;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// The Lecture Record — SRS I5
// ---------------------------------------------------------------------------

function readLecture(value: unknown): Lecture | null {
  if (!isRecord(value)) return null;
  const { id, filename, topic } = value;
  if (typeof id !== 'string' || typeof filename !== 'string') return null;

  const names = Array.isArray(value.participantNames)
    ? value.participantNames.filter((name): name is string => typeof name === 'string')
    : [];
  const number = (key: string): number =>
    typeof value[key] === 'number' && Number.isFinite(value[key]) ? value[key] : 0;

  return {
    id,
    filename,
    topic: typeof topic === 'string' ? topic : 'Untitled Topic',
    participantNames: names,
    pageCount: number('pageCount'),
    durationMinutes: number('durationMinutes'),
    endedAt: number('endedAt'),
    sizeBytes: number('sizeBytes'),
    isStarred: value.isStarred === true,
    isHot: value.isHot !== false,
    hasThumbnail: value.hasThumbnail === true,
  };
}

/** The lessons, and everything the dropdowns can offer — docs/06-UI-SPEC.md §11.2. */
export interface LectureRecord {
  readonly lectures: readonly Lecture[];
  readonly topics: readonly string[];
  readonly students: readonly string[];
}

function readNames(value: unknown): readonly string[] {
  return Array.isArray(value)
    ? value.filter((name): name is string => typeof name === 'string')
    : [];
}

/**
 * The finished lessons this teacher may see, narrowed by whatever they have typed.
 *
 * `query` is the address built by `filterQuery`. The narrowing happens in the
 * database, where the index is (SRS I5).
 */
export async function fetchLectures(query = ''): Promise<ApiResult<LectureRecord>> {
  try {
    const { status, body } = await get(`/api/lectures${query}`);
    if (status !== 200 || !isRecord(body) || !Array.isArray(body.lectures)) {
      return { ok: false, message: messageFrom(body, "We couldn't open your lessons.") };
    }
    const lectures: Lecture[] = [];
    for (const entry of body.lectures) {
      const lecture = readLecture(entry);
      if (lecture) lectures.push(lecture);
    }
    return {
      ok: true,
      value: { lectures, topics: readNames(body.topics), students: readNames(body.students) },
    };
  } catch {
    return { ok: false, message: OFFLINE_MESSAGE };
  }
}

/** SRS I5 — stars a lesson, or takes the star off. */
export async function setLectureStarred(
  lectureId: string,
  starred: boolean,
): Promise<ApiResult<boolean>> {
  try {
    const { status, body } = await post(`/api/lectures/${lectureId}/star`, { starred });
    if (status !== 200 || !isRecord(body)) {
      return { ok: false, message: messageFrom(body, "We couldn't change that lesson.") };
    }
    return { ok: true, value: body.starred === true };
  } catch {
    return { ok: false, message: OFFLINE_MESSAGE };
  }
}

/** A finished lesson, opened again — SRS I5, docs/06-UI-SPEC.md §11.3. */
export interface ResumedLesson {
  readonly boardId: string;
  readonly roomCode: string;
  readonly topic: string | null;
  /** True when the copy had to come back from Drive, which takes a moment. */
  readonly fromDrive: boolean;
}

/** Opens a finished lesson again, as a new class on the same board. */
export async function resumeLecture(lectureId: string): Promise<ApiResult<ResumedLesson>> {
  try {
    const { status, body } = await post(`/api/lectures/${lectureId}/resume`, {});
    if (status !== 200 || !isRecord(body)) {
      return { ok: false, message: messageFrom(body, "We couldn't open that lesson.") };
    }
    const { boardId, roomCode, topic } = body;
    if (typeof boardId !== 'string' || typeof roomCode !== 'string') {
      return { ok: false, message: "We couldn't open that lesson." };
    }
    return {
      ok: true,
      value: {
        boardId,
        roomCode,
        topic: typeof topic === 'string' ? topic : null,
        fromDrive: body.fromDrive === true,
      },
    };
  } catch {
    return { ok: false, message: OFFLINE_MESSAGE };
  }
}
